import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  FormControlLabel,
  Switch,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Send as SendIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { emailConfigApi, getDefaultAccountId } from '../services/api';
import { AlertInfo } from '../types';

interface EmailConfigManagerProps {
  showAlert: (message: string, severity: AlertInfo['severity']) => void;
  onConfigSelect?: (config: any) => void;
}

const EmailConfigManager: React.FC<EmailConfigManagerProps> = ({ showAlert, onConfigSelect }) => {
  const [configs, setConfigs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState<any>(null);
  const [testingConfig, setTestingConfig] = useState<any>(null);
  const [testEmail, setTestEmail] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    smtpHost: '',
    smtpPort: 587,
    smtpSecure: true,
    username: '',
    password: '',
    fromEmail: '',
    fromName: '',
    isDefault: false,
  });

  useEffect(() => {
    loadConfigs();
  }, []);

  const loadConfigs = async () => {
    try {
      setLoading(true);
      const accountId = getDefaultAccountId();
      const response = await emailConfigApi.getByAccount(accountId);
      setConfigs(response.configs || []);
    } catch (error) {
      console.error('Error loading email configs:', error);
      showAlert('Failed to load email configurations', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (config?: any) => {
    if (config) {
      setEditingConfig(config);
      setFormData({
        name: config.name,
        smtpHost: config.smtpHost,
        smtpPort: config.smtpPort,
        smtpSecure: config.smtpSecure,
        username: config.username,
        password: '', // Don't populate password for security
        fromEmail: config.fromEmail,
        fromName: config.fromName,
        isDefault: config.isDefault,
      });
    } else {
      setEditingConfig(null);
      setFormData({
        name: '',
        smtpHost: '',
        smtpPort: 587,
        smtpSecure: true,
        username: '',
        password: '',
        fromEmail: '',
        fromName: '',
        isDefault: false,
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingConfig(null);
  };

  const handleSave = async () => {
    try {
      const accountId = getDefaultAccountId();
      const data = {
        ...formData,
        accountId,
      };

      if (editingConfig) {
        const result = await emailConfigApi.update(editingConfig.id, formData);
        if (result.testResult) {
          showAlert(result.testResult.message, result.testResult.success ? 'success' : 'error');
        } else {
          showAlert('Email configuration updated successfully', 'success');
        }
      } else {
        const result = await emailConfigApi.create(data);
        if (result.testResult) {
          showAlert(result.testResult.message, result.testResult.success ? 'success' : 'error');
        } else {
          showAlert('Email configuration created successfully', 'success');
        }
      }

      handleCloseDialog();
      loadConfigs();
    } catch (error: any) {
      console.error('Error saving email config:', error);
      if (error.response?.data?.testResult) {
        showAlert(error.response.data.error || 'Configuration test failed', 'error');
      } else {
        showAlert('Failed to save email configuration', 'error');
      }
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this email configuration?')) {
      return;
    }

    try {
      await emailConfigApi.delete(id);
      showAlert('Email configuration deleted successfully', 'success');
      loadConfigs();
    } catch (error) {
      console.error('Error deleting email config:', error);
      showAlert('Failed to delete email configuration', 'error');
    }
  };

  const handleTestConnection = async (config: any) => {
    try {
      const result = await emailConfigApi.test(config.id);
      showAlert(
        result.success ? 'Connection test successful' : 'Connection test failed',
        result.success ? 'success' : 'error'
      );
    } catch (error) {
      console.error('Error testing connection:', error);
      showAlert('Connection test failed', 'error');
    }
  };

  const handleSendTestEmail = async () => {
    if (!testingConfig || !testEmail) return;

    try {
      const result = await emailConfigApi.sendTestEmail(testingConfig.id, {
        to: testEmail,
        subject: 'Test Email from Email Outreach Platform',
      });
      
      showAlert(
        result.success ? 'Test email sent successfully' : 'Failed to send test email',
        result.success ? 'success' : 'error'
      );
      
      setTestDialogOpen(false);
      setTestEmail('');
      setTestingConfig(null);
    } catch (error) {
      console.error('Error sending test email:', error);
      showAlert('Failed to send test email', 'error');
    }
  };

  const openTestDialog = (config: any) => {
    setTestingConfig(config);
    setTestDialogOpen(true);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, margin: 'auto', padding: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Email Configurations</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Configuration
        </Button>
      </Box>

      {configs.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No email configurations yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Add your first email configuration to start sending campaigns
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
            Add First Configuration
          </Button>
        </Paper>
      ) : (
        <List>
          {configs.map((config) => (
            <Paper key={config.id} sx={{ mb: 2 }}>
              <ListItem>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {config.name}
                      {config.isDefault && (
                        <Chip label="Default" color="primary" size="small" />
                      )}
                      {config.isValid === true && (
                        <Chip label="✓ Verified" color="success" size="small" />
                      )}
                      {config.isValid === false && (
                        <Chip label="⚠ Invalid" color="error" size="small" />
                      )}
                      {config.isValid === null && (
                        <Chip label="? Untested" color="warning" size="small" />
                      )}
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {config.fromName} &lt;{config.fromEmail}&gt;
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {config.smtpHost}:{config.smtpPort} • {config.smtpSecure ? 'Secure' : 'Insecure'}
                      </Typography>
                    </Box>
                  }
                />
                <ListItemSecondaryAction>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {onConfigSelect && (
                      <Button
                        size="small"
                        onClick={() => onConfigSelect(config)}
                      >
                        Select
                      </Button>
                    )}
                    <IconButton
                      size="small"
                      onClick={() => handleTestConnection(config)}
                      title="Test Connection"
                    >
                      <CheckIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => openTestDialog(config)}
                      title="Send Test Email"
                    >
                      <SendIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(config)}
                      title="Edit"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(config.id)}
                      title="Delete"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </ListItemSecondaryAction>
              </ListItem>
            </Paper>
          ))}
        </List>
      )}

      {/* Configuration Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingConfig ? 'Edit Email Configuration' : 'Add Email Configuration'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Configuration Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Gmail, Outlook, Custom SMTP"
              />
            </Grid>
            
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="SMTP Host"
                value={formData.smtpHost}
                onChange={(e) => setFormData({ ...formData, smtpHost: e.target.value })}
                placeholder="e.g., smtp.gmail.com"
              />
            </Grid>
            
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="SMTP Port"
                type="number"
                value={formData.smtpPort}
                onChange={(e) => setFormData({ ...formData, smtpPort: parseInt(e.target.value) })}
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.smtpSecure}
                    onChange={(e) => setFormData({ ...formData, smtpSecure: e.target.checked })}
                  />
                }
                label="Use Secure Connection (TLS/SSL)"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Username"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                placeholder="SMTP username"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="SMTP password"
                helperText={editingConfig ? "Leave blank to keep current password" : ""}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="From Email"
                type="email"
                value={formData.fromEmail}
                onChange={(e) => setFormData({ ...formData, fromEmail: e.target.value })}
                placeholder="sender@example.com"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="From Name"
                value={formData.fromName}
                onChange={(e) => setFormData({ ...formData, fromName: e.target.value })}
                placeholder="Your Name or Company"
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isDefault}
                    onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                  />
                }
                label="Set as Default Configuration"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            {editingConfig ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Test Email Dialog */}
      <Dialog open={testDialogOpen} onClose={() => setTestDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Send Test Email</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Test Email Address"
            type="email"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            placeholder="test@example.com"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSendTestEmail}
            variant="contained"
            disabled={!testEmail}
          >
            Send Test Email
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmailConfigManager;
